import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, FileText, Images, X, Loader2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, refreshSession } from "@/lib/api";
import { Button } from "@/components/kit";

export function AuthImage({ path, className = "", onClick, testid }) {
  const [loaded, setLoaded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const onError = () => {
    if (attempt) return setFailed(true);
    refreshSession().catch(() => {}).finally(() => setAttempt(1));
  };
  if (failed) return <div className={`${className} flex items-center justify-center muted`} style={{ background: "var(--surface-alt)" }} title="Foto indisponível" data-testid={testid}><ImageOff size={16} /></div>;
  return (
    <div className={`${className} relative overflow-hidden`} style={{ background: "var(--surface-alt)" }}>
      {!loaded && <Loader2 size={16} className="animate-spin muted absolute inset-0 m-auto" />}
      <img src={`${fileUrl(path)}${attempt ? "?r=1" : ""}`} alt="Nota fiscal" onLoad={() => setLoaded(true)} onError={onError} onClick={onClick} data-testid={testid}
        className={`w-full h-full object-cover cursor-zoom-in ${loaded ? "" : "opacity-0"}`} />
    </div>
  );
}

export function PhotoViewer({ path, onClose }) {
  useEffect(() => {
    if (!path) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [path, onClose]);
  if (!path) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.88)" }} onClick={onClose} data-testid="photo-viewer">
      <button className="absolute top-4 right-4 icon-btn" onClick={onClose} data-testid="close-photo-viewer"><X size={18} /></button>
      <div onClick={(e) => e.stopPropagation()}>
        <AuthImage path={path} className="w-[min(92vw,900px)] h-[85vh] rounded-xl [&>img]:!object-contain [&>img]:!cursor-default" testid="photo-viewer-image" />
      </div>
    </div>,
    document.body,
  );
}

export default function PhotoCapture({ onUploaded, compact, cameraLabel = "Fotografar nota", allowPdf = false }) {
 const galAccept = allowPdf ? "image/png,image/jpeg,image/webp,application/pdf" : "image/png,image/jpeg,image/webp";
  const cam = useRef();
  const gal = useRef();
  const [loading, setLoading] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f, f.name || "nota.jpg");
      const r = await api("/upload", { method: "POST", form: fd });
      onUploaded(r.path);
      toast.success("Foto anexada");
    } catch (err) {
      toast.error(err.message || "Falha ao enviar a foto");
    } finally {
      setLoading(false);
    }
  };
  if (compact)
    return (
      <>
        <input ref={gal} type="file" accept={allowPdf ? "image/*,application/pdf" : "image/*"} hidden onChange={pick} data-testid="invoice-attach-input" />
        <button className="icon-btn" disabled={loading} onClick={() => gal.current.click()} title="Anexar foto" data-testid="invoice-attach-photo-button">
          {loading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
        </button>
      </>
    );
  return (
    <div className="flex gap-2">
      <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={pick} data-testid="invoice-photo-camera-input" />
      <input ref={gal} type="file" accept={galAccept} hidden onChange={pick} data-testid="invoice-photo-gallery-input" />
      <Button type="button" variant="secondary" size="sm" icon={Camera} loading={loading} onClick={() => cam.current.click()} data-testid="invoice-photo-camera-button">
        {compact ? "" : cameraLabel}
      </Button>
      <Button type="button" variant="secondary" size="sm" icon={Images} disabled={loading} onClick={() => gal.current.click()} data-testid="invoice-photo-gallery-button">
        {compact ? "" : "Galeria"}
      </Button>
    </div>
  );
}

export const isPdf = (p) => /\.pdf$/i.test(p || "");
export function PdfTile({ path, className = "w-20 h-20", testid }) {
 return <a href={fileUrl(path)} target="_blank" rel="noreferrer" className={`${className} rounded-lg flex flex-col items-center justify-center gap-1 caption`} style={{ background: "var(--surface-alt)" }} data-testid={testid}><FileText size={24} />PDF</a>;
}
